import { PageContainer, PageHeader, Spread, Text, VStack } from '@/components/Atoms'
import CommentsHistoryDrawer from '@/components/Comments/CommentsHistoryDrawer'
import { LastCommentsCard } from '@/components/Comments/LastCommentsCard'
import { IntroductionDialog } from '@/components/IntroductionDialog'
import { PageError } from '@/components/PageError'
import { PageLoader } from '@/components/PageLoader'
import { useAccountComments } from '@/hooks/queries/useComments'
import { storeDetailsQueryOptions, useStoreDetails } from '@/hooks/queries/useStores'
import { formatAddress } from '@/lib/utils/addresses'
import { queryClient } from '@/main'
import { INTRO_DIALOGS } from '@/stores/tutorials'
import { Button } from '@repo/ui/components'
import { createFileRoute } from '@tanstack/react-router'
import { MapPinIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import AccountCommentModal from './-components/AccountCommentModal'
import { StoreCrosslinksCard } from './-components/StoreCrosslinksCard'
import { StoreDetailsCard } from './-components/StoreDetailsCard'

export const Route = createFileRoute('/_auth/_modules/_app/customers/stores/$storeId')({
  component: StoreDetail,
  loader: async ({ params: { storeId } }) => {
    const store = await queryClient.ensureQueryData(storeDetailsQueryOptions(storeId))

    return {
      crumb: store.sapErp,
    }
  },
})

function StoreDetail() {
  const { t } = useTranslation('stores')

  const { storeId } = Route.useParams()

  const {
    isPending: isStoreDetailsPending,
    error: storeDetailsError,
    data: store,
  } = useStoreDetails(storeId)
  const {
    isPending: isAccountCommentsPending,
    error: accountCommentsError,
    data: comments = [],
  } = useAccountComments(storeId)

  const [isCommentModalOpen, setIsCommentModalOpen] = useState<boolean>(false)
  const [isCommentsHistoryDrawerOpen, setIsCommentsHistoryDrawerOpen] = useState<boolean>(false)
  const [editedCommentId, setEditedCommentId] = useState<number | undefined>(undefined)

  const handleOnCommentModalOpen = (id?: number) => {
    setIsCommentModalOpen(true)
    setEditedCommentId(id ?? undefined)
  }

  if (!store && (isStoreDetailsPending || isAccountCommentsPending)) return <PageLoader />

  if (!store && (storeDetailsError || accountCommentsError))
    return <PageError message={storeDetailsError?.message ?? accountCommentsError?.message} />

  return (
    <PageContainer>
      <IntroductionDialog
        dialogId={INTRO_DIALOGS.STORE_DETAILS}
        title={t('tutorials.storeDetails.dialog.title')}
        guideUrl="/customers/view-store-details"
      >
        {t('tutorials.storeDetails.dialog.description')}
      </IntroductionDialog>

      <Spread align="start">
        <VStack gap="sm">
          <PageHeader>{store.name}</PageHeader>
          <Text tone="muted" weight="medium">
            ERP {store.sapErp}
          </Text>
        </VStack>

        <VStack gap="sm">
          <Button
            variant="link"
            size="sm"
            className="text-muted-foreground hover:text-foreground p-0 pt-1"
            asChild
          >
            <a
              href={`https://maps.google.com/maps?q=${encodeURIComponent(`${store?.addressLine1} ${store?.city} ${store?.provinceCode}`)}`}
              target="_blank"
              className="flex items-center gap-1"
            >
              <MapPinIcon className="-mt-1 size-3 shrink-0" />
              <span className="text-right leading-relaxed ">
                {formatAddress(
                  store?.addressLine1,
                  store?.addressLine2,
                  store?.city,
                  store?.provinceCode,
                ).toUpperCase()}
              </span>
            </a>
          </Button>
        </VStack>
      </Spread>

      <LastCommentsCard
        comments={comments}
        handleOnCommentModalOpen={handleOnCommentModalOpen}
        setIsCommentsHistoryDrawerOpen={setIsCommentsHistoryDrawerOpen}
        guideUrl="/customers/comments/leave-comments"
      />

      <StoreDetailsCard storeId={storeId} />

      <StoreCrosslinksCard storeId={storeId} />

      <AccountCommentModal
        accountName={store.name ?? ''}
        isOpen={isCommentModalOpen}
        sapErp={storeId}
        setIsOpen={setIsCommentModalOpen}
        comment={comments.find((comment) => comment.id === editedCommentId)}
      />

      <CommentsHistoryDrawer
        comments={comments}
        error={accountCommentsError}
        isPending={isAccountCommentsPending}
        isOpen={isCommentsHistoryDrawerOpen}
        handleOnCommentModalOpen={handleOnCommentModalOpen}
        setIsOpen={setIsCommentsHistoryDrawerOpen}
        guideUrl="/customers/comments/leave-comments"
      />
    </PageContainer>
  )
}
